"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import {
  loadCatalog,
  type Catalog,
} from "@/lib/i18n/loadCatalog";
import { writeLocaleCookie } from "@/lib/i18n/cookie";
import { resolveLocale } from "@/lib/i18n/resolveLocale";
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  normalizeLocale,
  type Locale,
} from "@/lib/i18n/locales";
import { interpolate, type InterpolationParams } from "@/lib/i18n/interpolate";

interface I18nContextValue {
  locale: Locale;
  /** Translate a key with optional `{placeholder}` params. Falls back to en, then key. */
  t: (key: string, params?: InterpolationParams) => string;
  /** Set the locale and persist (cookie + Firestore if signed in). */
  setLocale: (locale: Locale) => Promise<void>;
  /**
   * Detected device locale (or null if we can't read navigator.language).
   * Used by LocalePromptBanner to suggest a switch on first launch.
   */
  deviceLocale: Locale | null;
  /** Source of the active locale resolution (debug/telemetry only). */
  source: "profile" | "cookie" | "navigator" | "default";
  /** Whether the catalog for the active locale has loaded. */
  ready: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const { profile } = useHouseholdContext();

  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
  const [source, setSource] =
    useState<I18nContextValue["source"]>("default");
  const [enCatalog, setEnCatalog] = useState<Catalog | null>(null);
  const [activeCatalog, setActiveCatalog] = useState<Catalog | null>(null);
  const [deviceLocale, setDeviceLocale] = useState<Locale | null>(null);

  // Always preload `en` once — used as the fallback for missing keys.
  useEffect(() => {
    let cancelled = false;
    loadCatalog("en").then((cat) => {
      if (!cancelled) setEnCatalog(cat);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // On mount, capture the navigator/device-language guess once.
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setDeviceLocale(normalizeLocale(navigator.language));
  }, []);

  // Resolve the effective locale whenever profile changes.
  useEffect(() => {
    const resolved = resolveLocale({
      profileLocale: profile?.locale ?? null,
      navigatorLanguage:
        typeof navigator !== "undefined" ? navigator.language : null,
    });
    setLocaleState(resolved.locale);
    setSource(resolved.source);
  }, [profile?.locale]);

  // Load the active locale's catalog when it changes.
  useEffect(() => {
    let cancelled = false;
    loadCatalog(locale).then((cat) => {
      if (!cancelled) setActiveCatalog(cat);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback(
    async (next: Locale) => {
      writeLocaleCookie(next);
      setLocaleState(next);
      setSource("profile");
      if (user?.uid) {
        try {
          await updateDoc(doc(db, "member_profiles", user.uid), {
            locale: next,
          });
        } catch (err) {
          console.error("Failed to persist locale to Firestore", err);
        }
      }
    },
    [user?.uid],
  );

  const t = useCallback<I18nContextValue["t"]>(
    (key, params) => {
      const value =
        activeCatalog?.[key] ?? enCatalog?.[key] ?? key;
      return interpolate(value, params);
    },
    [activeCatalog, enCatalog],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      t,
      setLocale,
      deviceLocale,
      source,
      ready: activeCatalog !== null && enCatalog !== null,
    }),
    [locale, t, setLocale, deviceLocale, source, activeCatalog, enCatalog],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useTranslations(): I18nContextValue["t"] {
  return useI18n().t;
}

export function useLocale(): Locale {
  return useI18n().locale;
}

export { isSupportedLocale };
